import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/* eslint-disable max-lines */
import * as React from 'react';
import { EEventType } from '../../types/events';
import { EKeyCode } from '../../types/keyCodes';
import { isEmail, isValidCode, isValidPhone } from '../../utils/validation';
import { InputField } from '../../_NEXT_components/InputField';
import { EAuthenticateStatus, ETwoFactorStatus, ETwoFactorType } from '../../types/authentication';
import { CountdownTimer } from '../../_NEXT_components/CountdownTimer';
import { Link } from '../../_NEXT_components/Link';
import { Help } from '../../_NEXT_components/Help';
import { Button, EButtonIntent } from '../../_NEXT_components/Button';
import { TitleContainer } from '../TitleContainer';
import { ViewContainer } from '../ViewContainer';
import { FormFieldHelper } from '../../_NEXT_components/FormFieldHelper';
import { ContentLayout } from '../../_NEXT_components/ContentLayout';
import { PhoneField } from '../../_NEXT_components/PhoneField';
import { EActiveView } from '../../types/view';
import { trackTwoFactorCannotConfirmClick, trackTwoFactorClose, trackTwoFactorContinueClick, trackTwoFactorPhoneInputClick, trackTwoFactorPopupOpen, trackTwoFactorResendClick, trackTwoFactorWhatForClick } from '../../utils/tracking';
import { FormFieldGroupLayout } from '../../_NEXT_components/FieldGroupLayout';
import { SubtitleLayout } from '../../_NEXT_components/SubtitleLayout';
export function TwoFactorViewContainer(_ref) {
  var login = _ref.login,
      authenticateStatus = _ref.authenticateStatus,
      twoFactorStatus = _ref.twoFactorStatus,
      phoneError = _ref.phoneError,
      hasCodeError = _ref.hasCodeError,
      codeError = _ref.codeError,
      sendTwoFactorCode = _ref.sendTwoFactorCode,
      authenticate = _ref.authenticate,
      changeLogin = _ref.changeLogin,
      changeView = _ref.changeView,
      reset = _ref.reset,
      emit = _ref.emit;

  // FORM
  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isChanged = _React$useState2[0],
      setIsChanged = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      isTouched = _React$useState4[0],
      setIsTouched = _React$useState4[1];

  var prevTwoFactorStatus = React.useRef(twoFactorStatus);
  var isSubmitDisabled = React.useMemo(function () {
    return [EAuthenticateStatus.Loading, EAuthenticateStatus.Succeed].includes(authenticateStatus) || twoFactorStatus === ETwoFactorStatus.Loading;
  }, [authenticateStatus, twoFactorStatus]); // PHONE

  var phoneRef = React.useRef(null);

  var _React$useState5 = React.useState(''),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      phone = _React$useState6[0],
      setPhone = _React$useState6[1];

  var _React$useState7 = React.useState(false),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      isPhoneInvalid = _React$useState8[0],
      setPhoneInvalid = _React$useState8[1];

  var isPhoneDisabled = React.useMemo(function () {
    return twoFactorStatus === ETwoFactorStatus.Loading || twoFactorStatus === ETwoFactorStatus.Succeed;
  }, [twoFactorStatus]);
  var handlePhoneChange = React.useCallback(function (e) {
    var value = e.currentTarget.value;
    var phone = value.replace(/\D/gi, '');
    setIsChanged(true);
    setPhone(phone);
    setPhoneInvalid(!!phone && !isValidPhone(phone));
  }, []); // CODE

  var codeRef = React.useRef(null);

  var _React$useState9 = React.useState(''),
      _React$useState10 = _slicedToArray(_React$useState9, 2),
      code = _React$useState10[0],
      setCode = _React$useState10[1];

  var _React$useState11 = React.useState(false),
      _React$useState12 = _slicedToArray(_React$useState11, 2),
      isCodeInvalid = _React$useState12[0],
      setCodeInvalid = _React$useState12[1];

  var _React$useState13 = React.useState(false),
      _React$useState14 = _slicedToArray(_React$useState13, 2),
      codeResendAvailable = _React$useState14[0],
      setCodeResendAvailable = _React$useState14[1];

  var _React$useState15 = React.useState(undefined),
      _React$useState16 = _slicedToArray(_React$useState15, 2),
      codeSentTimestamp = _React$useState16[0],
      setCodeSentTimestamp = _React$useState16[1];

  var isCodeVisible = React.useMemo(function () {
    return twoFactorStatus === ETwoFactorStatus.Succeed || twoFactorStatus !== ETwoFactorStatus.Failed && codeResendAvailable;
  }, [twoFactorStatus, codeResendAvailable]);
  var isCodeDisabled = React.useMemo(function () {
    return [EAuthenticateStatus.Loading, EAuthenticateStatus.Succeed].includes(authenticateStatus);
  }, [authenticateStatus]); // HELP

  var contactQuery = React.useMemo(function () {
    return [isEmail(login) ? "email=".concat(encodeURIComponent(login)) : null, phone && isValidPhone(phone) ? "phone=".concat(encodeURIComponent("+".concat(phone.replace(/\D+/g, '')))) : null, 'twoFactor=true'].filter(Boolean).join('&');
  }, [phone, login]);
  var handleWhatForClick = React.useCallback(function () {
    trackTwoFactorWhatForClick(ETwoFactorType.Sms);
    emit(EEventType.TwoFactorWhatForClick);
  }, [emit]); // SUBMIT

  var validateCode = React.useCallback(function (code) {
    if (!isValidCode(code)) {
      setCodeInvalid(true);

      if (codeRef.current) {
        codeRef.current.focus();
      }

      throw new Error('invalid');
    }

    setIsChanged(false);
    authenticate({
      phone: phone,
      code: code
    });
  }, [authenticate, phone]); // CODE

  var handleCodeChange = React.useCallback(function (e) {
    var value = e.currentTarget.value;
    var code = value.replace(/\D/gi, '');

    if (code.length > 4) {
      return;
    }

    setCode(code);
    setIsChanged(true);
    setCodeInvalid(isValidCode(code));

    try {
      validateCode(code);
    } catch (e) {
      return;
    }
  }, [validateCode]); // SUBMIT

  var handleSubmit = React.useCallback(function () {
    setIsTouched(true);

    if ([EAuthenticateStatus.Loading, EAuthenticateStatus.Succeed].includes(authenticateStatus)) {
      return;
    }

    if (twoFactorStatus === ETwoFactorStatus.Loading) {
      return;
    }

    var formattedPhone = isValidPhone(phone) ? "+".concat(phone.replace(/\D+/g, '')) : '';

    if (twoFactorStatus !== ETwoFactorStatus.Succeed) {
      if (!formattedPhone) {
        setPhoneInvalid(true);
        return;
      }

      setIsChanged(false);
      sendTwoFactorCode(phone);
    } else {
      try {
        validateCode(code);
      } catch (e) {
        return;
      }
    }

    emit(EEventType.TwoFactorContinueClick);
  }, [twoFactorStatus, authenticateStatus, phone, code, emit, validateCode, sendTwoFactorCode]);
  var handleSubmitClick = React.useCallback(function () {
    trackTwoFactorContinueClick(ETwoFactorType.Sms);
    handleSubmit();
  }, [handleSubmit]);
  var handleTimeoutExpired = React.useCallback(function () {
    setCodeResendAvailable(true);
  }, []);
  var handleResendClick = React.useCallback(function (event) {
    event.preventDefault();

    if (twoFactorStatus !== ETwoFactorStatus.Succeed) {
      return;
    }

    trackTwoFactorResendClick(ETwoFactorType.Sms);
    setIsChanged(true);
    setCodeInvalid(false);
    setCode('');
    sendTwoFactorCode("+".concat(phone.replace(/\D+/g, '')));
    emit(EEventType.TwoFactorResendClick);
  }, [emit, sendTwoFactorCode, twoFactorStatus, phone]);
  var handleCannotConfirmClick = React.useCallback(function () {
    trackTwoFactorCannotConfirmClick(ETwoFactorType.Sms);
    emit(EEventType.TwoFactorCannotConfirmClick);
  }, [emit]);
  var handleBackClick = React.useCallback(function () {
    trackTwoFactorClose(ETwoFactorType.Sms);
    reset();
    changeLogin(login);
    changeView(EActiveView.AuthenticationRegistration);
  }, [changeLogin, changeView, reset, login]);
  var handlePreventClick = React.useCallback(function (e) {
    e.preventDefault();
  }, []);
  var handlePreventSubmit = React.useCallback(function (e) {
    e.preventDefault();
  }, []);
  var handleSubmitOnEnter = React.useCallback(function (e) {
    if (e.keyCode === EKeyCode.Enter) {
      handleSubmit();
    }
  }, [handleSubmit]);
  React.useEffect(function () {
    if (twoFactorStatus !== prevTwoFactorStatus.current) {
      switch (twoFactorStatus) {
        case ETwoFactorStatus.Failed:
          setCodeSentTimestamp(undefined);
          setCodeResendAvailable(false);
          break;

        case ETwoFactorStatus.Succeed:
          setCodeSentTimestamp(new Date().getTime());
          setCodeResendAvailable(false);

          if (codeRef.current) {
            codeRef.current.focus();
          }

          break;
      }
    }

    prevTwoFactorStatus.current = twoFactorStatus;
  }, [twoFactorStatus]);
  React.useEffect(function () {
    trackTwoFactorPopupOpen(ETwoFactorType.Sms);
  }, []);
  return /*#__PURE__*/React.createElement(ViewContainer, null, /*#__PURE__*/React.createElement(TitleContainer, null), /*#__PURE__*/React.createElement(SubtitleLayout, null, /*#__PURE__*/React.createElement("div", null, "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u043D\u044B\u0439 \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430 \u0438\u043B\u0438 \u0442\u0435\u043B\u0435\u0444\u043E\u043D, \u043A\u043E\u0442\u043E\u0440\u044B\u0439 \u0443\u043A\u0430\u0437\u0430\u043D \u0432 \u0432\u0430\u0448\u0435\u043C \u043E\u0431\u044A\u044F\u0432\u043B\u0435\u043D\u0438\u0438. \u0422\u043E\u043B\u044C\u043A\u043E \u043C\u043E\u0431\u0438\u043B\u044C\u043D\u044B\u0435 \u043D\u043E\u043C\u0435\u0440\u0430."), /*#__PURE__*/React.createElement(Link, {
    href: "/help/security/37947/",
    target: "_blank",
    onClick: handleWhatForClick
  }, "\u0417\u0430\u0447\u0435\u043C \u044D\u0442\u043E \u043D\u0443\u0436\u043D\u043E")), /*#__PURE__*/React.createElement(ContentLayout, null, /*#__PURE__*/React.createElement("form", {
    onSubmit: handlePreventSubmit
  }, /*#__PURE__*/React.createElement(FormFieldGroupLayout, null, /*#__PURE__*/React.createElement(PhoneField, {
    autoFocus: true,
    name: "phone",
    ref: phoneRef,
    id: "two_factor-phone",
    value: phone,
    onFocus: trackTwoFactorPhoneInputClick,
    errorMessage: twoFactorStatus === ETwoFactorStatus.Failed && !isChanged ? phoneError : undefined,
    invalid: isTouched && (twoFactorStatus === ETwoFactorStatus.Failed && !isChanged || isPhoneInvalid),
    disabled: isPhoneDisabled,
    onKeyDown: handleSubmitOnEnter,
    onChange: handlePhoneChange
  }), isCodeVisible && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputField, {
    autoFocus: true,
    name: "code",
    ref: codeRef,
    id: "two_factor-code",
    placeholder: "\u041A\u043E\u0434 \u0438\u0437 \u0441\u043C\u0441",
    value: code,
    errorMessage: hasCodeError && !isChanged ? codeError : undefined,
    invalid: isTouched && (hasCodeError && !isChanged || isCodeInvalid),
    disabled: isCodeDisabled,
    onChange: handleCodeChange,
    onKeyDown: handleSubmitOnEnter
  }), codeSentTimestamp && !isSubmitDisabled ? /*#__PURE__*/React.createElement(FormFieldHelper, null, !codeResendAvailable ? /*#__PURE__*/React.createElement(CountdownTimer, {
    timeout: 60,
    fromTimestamp: codeSentTimestamp,
    onTimeoutExpired: handleTimeoutExpired
  }, function (_ref2) {
    var timeLeft = _ref2.timeLeft;
    return /*#__PURE__*/React.createElement(Link, {
      href: "#",
      onClick: handlePreventClick
    }, "\u0412\u044B\u0441\u043B\u0430\u0442\u044C \u043A\u043E\u0434 \u043F\u043E\u0432\u0442\u043E\u0440\u043D\u043E \u0447\u0435\u0440\u0435\u0437 ", timeLeft, " \u0441\u0435\u043A.");
  }) : /*#__PURE__*/React.createElement(Link, {
    href: "#",
    onClick: handleResendClick
  }, "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u043F\u043E\u0432\u0442\u043E\u0440\u043D\u043E")) : null)), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubmitClick,
    dataMark: "ResumeTwoFactor",
    intent: EButtonIntent.Primary,
    fill: true,
    loading: isSubmitDisabled,
    disabled: isSubmitDisabled
  }, "\u041F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C"), /*#__PURE__*/React.createElement(Button, {
    dataMark: "CancelTwoFactor",
    type: "button",
    fill: true,
    onClick: handleBackClick
  }, "\u041E\u0442\u043C\u0435\u043D\u0430"))), /*#__PURE__*/React.createElement(Help, {
    query: contactQuery,
    text: "\u041D\u0435 \u043C\u043E\u0433\u0443 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C",
    onClick: handleCannotConfirmClick
  }));
}